import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ArrowLeft, BarChart3, FileText, Save, Users } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Select, SelectItem } from '../components/ui/select';
import { ReportAllocationPage } from './admin-reports';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';
import AccessAndManageReports from '../../views/utilities/AccessAndManageReports';
import { isOwnerAccount } from '../lib/access';

const settingsPages = [
  {
    id: 'dashboard-reports',
    eyebrow: 'Dashboard Report Management',
    title: 'Dashboard Report Allocation',
    description: 'Select rendered and detailed reports for each dashboard slot.',
    Icon: BarChart3,
  },
  {
    id: 'user-reports',
    eyebrow: 'User Report Allocation',
    title: 'Reports Allocation',
    description: 'Assign available reports to organizations or users.',
    Icon: Users,
  },
  {
    id: 'owner-report-management',
    eyebrow: 'Owner Report Management',
    title: 'Report Management',
    description: 'Create, edit, and manage report access from the owner workspace.',
    Icon: FileText,
    ownerOnly: true,
  },
];

const dashboardSlots = [
  { index: 0, title: 'Dashboard Report', size: 'Large', wide: true },
  ...Array.from({ length: 6 }, (_, index) => ({
    index: index + 1,
    title: `Report Slot ${index + 2}`,
    size: 'Medium',
    wide: false,
  })),
];

export function SettingsPage() {
  const account = useSelector((state) => state.account || {});
  const [activePageId, setActivePageId] = useState(null);
  const isOwner = isOwnerAccount(account);
  const visibleSettingsPages = settingsPages.filter((page) => !page.ownerOnly || isOwner);
  const activePage = visibleSettingsPages.find((page) => page.id === activePageId);

  useEffect(() => {
    if (activePageId && !activePage) {
      setActivePageId(null);
    }
  }, [activePage, activePageId]);

  if (activePageId === 'user-reports') {
    return (
      <div className="pb-12">
        <div className="px-6 lg:px-10 pt-5">
          <Button type="button" size="sm" variant="outline" onClick={() => setActivePageId(null)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Settings
          </Button>
        </div>
        <ReportAllocationPage />
      </div>
    );
  }

  if (activePageId === 'owner-report-management' && isOwner) {
    return (
      <div className="pb-12">
        <div className="px-6 lg:px-10 pt-5">
          <Button type="button" size="sm" variant="outline" onClick={() => setActivePageId(null)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Settings
          </Button>
        </div>
        <div className="px-6 lg:px-10 py-5">
          <AccessAndManageReports />
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="Workspace"
        title={activePage?.title || 'Settings'}
        description={activePage?.description || 'Choose a settings area to manage.'}
        actions={activePage ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setActivePageId(null)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Settings
          </Button>
        ) : null}
      />

      <div className="px-6 lg:px-10 grid gap-5">
        {!activePage && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleSettingsPages.map(({ id, eyebrow, title, description, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActivePageId(id)}
                className="surface-card p-5 text-left transition-colors hover:border-[#071a2d] hover:bg-[#071a2d] hover:text-[#a7f3d0]"
              >
                <div className="flex items-start gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-primary">
                      {eyebrow}
                    </span>
                    <span className="mt-2 block text-lg font-semibold">{title}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">{description}</span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {activePageId === 'dashboard-reports' && <DashboardReportManagement />}
      </div>
    </div>
  );
}

function DashboardReportManagement() {
  const account = useSelector((state) => state.account || {});
  const [reports, setReports] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const fetchReports = async () => {
      setLoading(true);
      setMessage('');
      try {
        const email = account?.org_member?.email || account?.UserDetails?.email || account?.user?.email;
        const user = email
          ? await axiosInstance.get(`${configData.API_SERVER}/user/get_user_with_email?email=${encodeURIComponent(email)}`, { signal: controller.signal })
          : null;
        const userId = user?.data?.id || null;
        const response = userId
          ? await axiosInstance.get(`${configData.API_SERVER}/user/allocated-reports?user_id=${encodeURIComponent(userId)}`, { signal: controller.signal })
          : { data: [] };
        setReports(response?.data || []);
      } catch (error) {
        if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
          console.error('Error fetching report data', error);
          setMessage('Unable to load reports.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
    return () => controller.abort();
  }, [account]);

  const flatReports = useMemo(() => flattenReports(reports), [reports]);

  const handleAssignmentChange = (slotIndex, type, reportId) => {
    const selectedReport = flatReports.find((report) => String(report.id) === String(reportId));
    setAssignments((previous) => ({
      ...previous,
      [slotIndex]: {
        ...previous[slotIndex],
        [type]: selectedReport
          ? { reportId: selectedReport.id, embedURL: selectedReport.embedURL }
          : null,
      },
    }));
  };

  const saveAssignments = async () => {
    setSaving(true);
    setMessage('');
    try {
      const email = account?.org_member?.email || account?.user?.email;
      const userResponse = email
        ? await axiosInstance.get(`${configData.API_SERVER}/user/get_user_with_email?email=${email}`)
        : null;

      await axiosInstance.post(
        'https://api.projectivetech.com/user/save-report-assignments/',
        {
          email: userResponse?.data?.email || email,
          assignments,
        }
      );
      setMessage('Dashboard report assignments saved.');
    } catch (error) {
      console.error('Error saving assignments', error);
      setMessage('Failed to save dashboard report assignments.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="grid gap-5">
      <div className="surface-card p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary/10">
              <BarChart3 className="h-5 w-5 text-primary" />
            </span>
            <div>
              <h2 className="font-display text-lg font-semibold">Dashboard Report Slots</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose the rendered and detailed reports shown on the dashboard.
              </p>
            </div>
          </div>
          <Button onClick={saveAssignments} disabled={saving || loading}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Dashboard Reports'}
          </Button>
        </div>

        {message && (
          <div className="mt-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {message}
          </div>
        )}
      </div>

      {loading ? (
        <div className="surface-card p-10 text-center text-sm text-muted-foreground">
          Loading reports...
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {dashboardSlots.map((slot) => (
            <div key={slot.index} className={`surface-card p-5 ${slot.wide ? 'lg:col-span-3' : ''}`}>
              <div className="mb-4 flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary/10">
                  <FileText className="h-4 w-4 text-primary" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold">{slot.title}</h3>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{slot.size}</p>
                </div>
              </div>

              <div className={`grid gap-3 ${slot.wide ? 'md:grid-cols-2' : ''}`}>
                <ReportSelect
                  label="Rendered Report"
                  value={assignments[slot.index]?.rendered?.reportId || ''}
                  reports={flatReports}
                  onChange={(value) => handleAssignmentChange(slot.index, 'rendered', value)}
                />
                <ReportSelect
                  label="Detailed Report"
                  value={assignments[slot.index]?.detailed?.reportId || ''}
                  reports={flatReports}
                  onChange={(value) => handleAssignmentChange(slot.index, 'detailed', value)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ReportSelect({ label, value, reports, onChange }) {
  return (
    <label className="grid gap-2">
      <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onChange} className="h-10 w-full">
        <SelectItem value="">None</SelectItem>
        {reports.map((report) => (
          <SelectItem key={report.id} value={String(report.id)}>
            {report._label}
          </SelectItem>
        ))}
      </Select>
    </label>
  );
}

function flattenReports(reports) {
  const output = [];

  const walk = (items, parentLabel = '') => {
    (items || []).forEach((report) => {
      const label = report.title || report.name || 'Untitled report';
      output.push({
        ...report,
        _label: parentLabel ? `${parentLabel} / ${label}` : label,
      });
      if (report.children?.length) walk(report.children, label);
    });
  };

  walk(reports);
  return output;
}
